// `avatar uninstall` — Command 13 (v1.1).
// Gỡ Avatar khỏi project + auto-backup vào ~/.avatar/uninstall-backups/.
// Đối ứng với `avatar init`. Code khách (src/) giữ nguyên, trừ pre-push hook do
// Avatar cài (dead code v2, gỡ ở mọi repo trong .claude/repos.json — ticket #129).

import { join, relative } from "node:path";
import { confirm } from "@inquirer/prompts";
import boxen from "boxen";
import type { Command } from "commander";
import { appendAuditEntry } from "../lib/audit-log-appender.js";
import {
  createUninstallBackupSnapshot,
  reconcileExcludeBackupManifest,
  reconcileGitignoreBackupManifest,
  reconcileV3PrePushHooksBackupManifest,
} from "../lib/create-uninstall-backup-snapshot.js";
import { detectAvatarProjectArtifacts } from "../lib/detect-avatar-project-artifacts.js";
import { executeUninstallDeletion } from "../lib/execute-uninstall-deletion.js";
import { findReposWithBlockInGitignore } from "../lib/find-repos-with-block-in-gitignore.js";
import {
  findReposWithBlockInGitExclude,
  type PruneExcludeResult,
  pruneAvatarBlockFromRepoExcludes,
} from "../lib/prune-avatar-block-from-repo-excludes.js";
import {
  findReposWithAvatarPrePushHook,
  pruneStaleAvatarPrePushHooks,
} from "../lib/prune-stale-avatar-pre-push-hooks.js";
import { readCliVersion } from "../lib/read-cli-version-from-package-json.js";
import {
  type StripAvatarBlockFromReposGitignoreResult,
  stripAvatarBlockFromReposGitignore,
} from "../lib/strip-avatar-block-from-repo-gitignores.js";
import { chalk, log } from "../lib/terminal-logger.js";
import { uninstallGlobalToolsViaBrew } from "../lib/uninstall-global-tools-via-brew.js";
import { readReposManifest } from "../lib/workspace-repos-manifest-store.js";

interface UninstallOptions {
  yes?: boolean;
  // Commander v15 binds `--no-backup` to the POSITIVE attribute `backup`
  // (default true; false when --no-backup passed) — không có `noBackup`.
  backup?: boolean;
  keepPack?: boolean;
  keepSubmodule?: boolean;
  keepHooks?: boolean;
  dryRun?: boolean;
}

export function registerUninstallCommand(program: Command): void {
  program
    .command("uninstall")
    .description("Gỡ Avatar khỏi project — backup tự động (M11)")
    .option("--yes", "Skip confirm prompt")
    .option("--no-backup", "Không tạo backup trước khi xóa (nguy hiểm)")
    .option("--keep-pack", "Giữ pack .claude/pack/")
    .option("--keep-submodule", "(deprecated: dùng --keep-pack)")
    .option("--keep-hooks", "Giữ git hooks post-merge, pre-push")
    .option("--dry-run", "Hiển thị danh sách sẽ xóa, không thực thi")
    .action(async (opts: UninstallOptions) => {
      try {
        await runUninstall(opts);
      } catch (err) {
        log.error(err instanceof Error ? err.message : String(err));
        process.exit(1);
      }
    });
}

async function runUninstall(opts: UninstallOptions): Promise<void> {
  const projectRoot = process.cwd();
  const artifacts = detectAvatarProjectArtifacts(projectRoot);

  if (!artifacts.hasAnyArtifact) {
    log.info("Project chưa cài Avatar — không có gì để gỡ.");
    return;
  }

  const keepPack = opts.keepPack ?? opts.keepSubmodule;
  if (opts.keepSubmodule) {
    log.warn("--keep-submodule đã deprecated — dùng --keep-pack thay thế.");
  }

  // Gate DUY NHẤT quyết định có gỡ pre-push hook Avatar (v3, src/<repo>/.git/hooks/)
  // hay không — CHỈ 2 vế, KHÔNG có keepPack (phase-03 §Gate, chống hồi quy B2
  // vòng 3 debate). `--keep-pack` KHÔNG chặn snapshot (gate backup ở dưới chỉ
  // bằng `opts.backup !== false`) nên bản sao hook vẫn sống ở
  // `~/.avatar/uninstall-backups/…`, ngoài tầm `rm -rf .claude/` — an toàn.
  const canPruneHooks = !opts.keepHooks && opts.backup !== false;

  // Gate RIÊNG cho bước dọn ignore (ticket #138 phase-04, D4/Q6) — TÁCH HẲN
  // khỏi canPruneHooks, KHÔNG tái dùng:
  //   - `--keep-hooks` nói về GIT HOOKS, không liên quan ignore ⇒ KHÔNG chặn
  //     bước này. Tái dùng canPruneHooks sẽ IM LẶNG để lại block ở mọi repo;
  //     kết hợp self-heal đã gỡ sạch .gitignore, member thấy .claude/ vẫn bị
  //     ignore mà không tìm ra rule ở đâu (info/exclude vô hình với git status).
  //   - `--no-backup` thì PHẢI chặn: backupPath = null ở dưới và
  //     executeUninstallDeletion xoá .claude/ ngay sau ⇒ backup rơi về mặc định
  //     <workspaceRoot>/.claude/_backup sẽ bị xoá giây kế tiếp ⇒ sửa file người
  //     dùng KHÔNG có đường lùi.
  const canPruneIgnores = opts.backup !== false;

  // Danh sách repo (đọc-thuần, KHÔNG xoá gì) có pre-push hook Avatar — dùng cho
  // summary/dry-run VÀ truyền vào snapshot. Phải tính TRƯỚC printUninstallSummary
  // vì nhánh --dry-run return sớm ngay dưới; tính sau thì dry-run in danh sách rỗng.
  const reposWithAvatarHook = await findReposWithAvatarPrePushHook(projectRoot);
  // 2 finder đọc-thuần của #138 — cùng lý do "tính TRƯỚC summary" như trên.
  // excludeScan.uncheckable = git không chạy được ⇒ summary CẤM in ✓ "không có
  // gì để dọn" (danh sách rỗng lúc đó không mang nghĩa đó).
  const excludeScan = await findReposWithBlockInGitExclude(projectRoot);
  const reposWithGitignoreBlock = await findReposWithBlockInGitignore(projectRoot);

  // Show summary.
  printUninstallSummary(
    projectRoot,
    artifacts,
    opts,
    keepPack,
    reposWithAvatarHook,
    canPruneHooks,
    {
      exclude: excludeScan,
      gitignore: reposWithGitignoreBlock,
      canPrune: canPruneIgnores,
    },
  );

  if (opts.dryRun) {
    log.dim("--dry-run: kết thúc, không xóa.");
    return;
  }

  // Confirm.
  if (!opts.yes) {
    const ok = await confirm({
      message: "Tiếp tục gỡ Avatar?",
      default: false,
    });
    if (!ok) {
      log.info("Đã hủy.");
      return;
    }
  }

  // Backup (trừ khi --no-backup, tức opts.backup === false).
  let backupPath: string | null = null;
  if (opts.backup !== false) {
    backupPath = await createUninstallBackupSnapshot(projectRoot, artifacts, readCliVersion(), {
      v3PrePushRepos: canPruneHooks ? reposWithAvatarHook : [],
      excludeRepos: canPruneIgnores ? excludeScan.repos : [],
      gitignoreRepos: canPruneIgnores ? reposWithGitignoreBlock : [],
    });
    log.success(`Backup tạo tại: ${backupPath}`);
  }

  // ⛔ THỨ TỰ BẮT BUỘC (phase-03 §Architecture): pruner PHẢI chạy Ở ĐÂY — giữa
  // createUninstallBackupSnapshot (trên) và executeUninstallDeletion (dưới).
  // executeUninstallDeletion xoá cả .claude/ (chứa repos.json); đặt pruner SAU
  // nó thì readReposManifest trả [] → pruner no-op IM LẶNG, hook v3 sống sót
  // không lỗi không log — đúng bug gốc ticket #129. Test chống hồi quy:
  // test_uninstall_runs_pruner_before_claude_dir_deletion.
  let prunedHookBackups: { name: string; backupPath: string }[] = [];
  if (canPruneHooks) {
    const hooksBackupDir = backupPath ? join(backupPath, "hooks") : undefined;
    const pruneResult = await pruneStaleAvatarPrePushHooks(projectRoot, {
      backupDir: hooksBackupDir,
    });
    prunedHookBackups = pruneResult.backups;
    if (backupPath) {
      // Reconcile: chỉ giữ tên repo ĐÃ BACKUP THÀNH CÔNG — không thì manifest
      // nói dối (liệt kê repo rơi bucket `failed`, không có file thật trong hooks/).
      await reconcileV3PrePushHooksBackupManifest(
        backupPath,
        pruneResult.backups.map((b) => b.name),
      );
    }
    if (pruneResult.pruned.length > 0) {
      log.success(
        `Đã gỡ pre-push hook Avatar ở ${pruneResult.pruned.length} repo: ${pruneResult.pruned.join(", ")}`,
      );
    }
  } else {
    log.warn(
      "--no-backup/--keep-hooks: giữ nguyên pre-push hook Avatar trong src/<repo>/.git/hooks/ (gỡ tay hoặc chạy 'avatar sync').",
    );
  }

  // ⛔ VÙNG BẮT BUỘC (tiếp) — ticket #138 phase-04: dọn Avatar block ở src/<repo>.
  // VỊ TRÍ CÓ CHỦ ĐÍCH: NGOÀI khối `if (canPruneHooks)` vừa đóng ở dòng trên
  // (N2). Nhét vào TRONG khối đó thì vẫn chạy trước executeUninstallDeletion
  // nên mọi gate THỨ TỰ vẫn xanh — nhưng `--keep-hooks` sẽ chặn oan bước dọn
  // ignore ⇒ vô hiệu hoá đúng quyết định D4. Test khoá duy nhất bắt được:
  // test_uninstall_cleans_ignores_even_with_keep_hooks.
  if (canPruneIgnores) {
    const igBackupDir = backupPath ? join(backupPath, "repo-ignores") : undefined;
    const excludeResult = await pruneAvatarBlockFromRepoExcludes(projectRoot, {
      backupDir: igBackupDir,
    });
    // onlyRepos = TOÀN BỘ manifest — ngữ nghĩa NGƯỢC với sync/doctor (truyền
    // patchResult.patched). Ở đó `patched` là cổng chặn "chỉ gỡ rào cũ khi rào
    // mới đã dựng xong"; ở uninstall đang gỡ Avatar đi HẲN, không còn rào mới
    // nào cần giữ ⇒ lọc theo `patched` sẽ để sót block ở đúng những repo không
    // ghi được exclude. ĐỪNG "sửa cho nhất quán" thành patched.
    const repos = await readReposManifest(projectRoot);
    const gitignoreResult = await stripAvatarBlockFromReposGitignore(projectRoot, {
      onlyRepos: repos.map((r) => r.name),
      backupDir: igBackupDir,
    });
    if (backupPath) {
      // 2 reconcile RIÊNG (N3): 2 pruner ghi chung repo-ignores/ nhưng mỗi
      // manifest field chỉ được phản ánh artifact của pruner tương ứng.
      await reconcileExcludeBackupManifest(
        backupPath,
        excludeResult.backups.map((b) => b.name),
      );
      await reconcileGitignoreBackupManifest(
        backupPath,
        gitignoreResult.backups.map((b) => b.name),
      );
    }
    logRepoIgnorePruneResults(excludeResult, gitignoreResult);
  } else {
    // BẮT BUỘC, không phải trang trí: info/exclude VÔ HÌNH với `git status`, và
    // tới lúc này .gitignore đã bị self-heal gỡ ⇒ không log thì user không còn
    // dấu vết nào để lần ra Avatar còn sót rule trong repo của họ.
    log.warn(
      "--no-backup: giữ nguyên Avatar block trong <gitdir>/info/exclude của src/<repo> " +
        "(gỡ tay, hoặc chạy lại 'avatar uninstall' không kèm --no-backup).",
    );
  }

  // Delete artifacts.
  await executeUninstallDeletion(artifacts, {
    keepPack,
    keepHooks: opts.keepHooks,
  });

  // Gỡ binary global avatar đã cài (gitnexus + gitleaks + trivy). User chốt
  // 2026-06-14: gỡ luôn không hỏi. Fail-soft. (Workspace khác sẽ tự cài lại khi sync.)
  uninstallGlobalToolsViaBrew();

  await appendAuditEntry("uninstall", `project=${projectRoot},backup=${backupPath ?? "skipped"}`);

  printUninstallSuccessBox(backupPath, prunedHookBackups);
}

// Log kết quả 2 pruner ignore. Tách hàm để runUninstall không phình — mọi
// nhánh "im lặng" ở đây đều là lỗi UX thật: file info/exclude không hiện trong
// `git status`, user không tự phát hiện được block còn sót.
function logRepoIgnorePruneResults(
  excludeResult: PruneExcludeResult,
  gitignoreResult: StripAvatarBlockFromReposGitignoreResult,
): void {
  if (excludeResult.gitOutcome !== "ok") {
    // CẤM dùng thông điệp "không phải git repo" ở đây — sai sự thật (B3/F1).
    const reason =
      excludeResult.gitOutcome === "gitUnavailable"
        ? "không tìm thấy git trên máy"
        : "git quá cũ, cần >= 2.31";
    log.warn(
      `git không chạy được (${reason}) — KHÔNG gỡ được block trong <gitdir>/info/exclude của repo nào. ` +
        "Chạy lại 'avatar uninstall' sau khi cài git >= 2.31.",
    );
  } else if (excludeResult.stripped.length > 0) {
    log.success(
      `Đã gỡ Avatar block khỏi <gitdir>/info/exclude ở ${excludeResult.stripped.length} repo: ${excludeResult.stripped.join(", ")}`,
    );
  }
  if (excludeResult.corrupted.length > 0) {
    log.warn(
      `info/exclude có marker Avatar hỏng (KHÔNG tự sửa, gỡ tay): ${excludeResult.corrupted.join(", ")}`,
    );
  }
  for (const f of excludeResult.failed) {
    log.warn(`Không gỡ được block trong info/exclude của "${f.name}": ${f.error}`);
  }

  if (gitignoreResult.stripped.length > 0) {
    log.success(
      `Đã gỡ Avatar block khỏi .gitignore ở ${gitignoreResult.stripped.length} repo: ${gitignoreResult.stripped.join(", ")}`,
    );
  }
  if (gitignoreResult.removedGitignore.length > 0) {
    // Xoá file của người dùng KHÔNG được im lặng (D3).
    log.info(
      `.gitignore chỉ còn Avatar block nên đã XOÁ hẳn ở: ${gitignoreResult.removedGitignore.join(", ")}`,
    );
  }
  if (gitignoreResult.corrupted.length > 0) {
    log.warn(
      `.gitignore có marker Avatar hỏng (KHÔNG tự sửa, gỡ tay): ${gitignoreResult.corrupted.join(", ")}`,
    );
  }
  for (const f of gitignoreResult.failed) {
    log.warn(`Không gỡ được block trong .gitignore của "${f.name}": ${f.error}`);
  }
}

interface RepoIgnoreScan {
  exclude: { repos: string[]; uncheckable: boolean };
  gitignore: string[];
  canPrune: boolean;
}

function printUninstallSummary(
  projectRoot: string,
  artifacts: ReturnType<typeof detectAvatarProjectArtifacts>,
  opts: UninstallOptions,
  keepPack: boolean | undefined,
  reposWithAvatarHook: string[],
  canPruneHooks: boolean,
  ignoreScan: RepoIgnoreScan,
): void {
  log.info(`Project: ${projectRoot}`);
  log.plain("");
  log.plain("Các artifact sẽ gỡ:");
  if (artifacts.claudeDir)
    log.plain(`  ${chalk.red("✗")} ${relative(projectRoot, artifacts.claudeDir) || ".claude/"}`);
  if (artifacts.claudeMd) log.plain(`  ${chalk.red("✗")} CLAUDE.md`);
  if (artifacts.postMergeHook && !opts.keepHooks) {
    log.plain(`  ${chalk.red("✗")} .git/hooks/post-merge`);
  }
  if (artifacts.prePushHook && !opts.keepHooks) {
    log.plain(`  ${chalk.red("✗")} .git/modules/src/hooks/pre-push`);
  }
  // v3 pre-push hook (src/<repo>/.git/hooks/pre-push) — tách khỏi 2 dòng legacy
  // v2 ở trên. Chỉ liệt kê khi THẬT SỰ sẽ bị gỡ (canPruneHooks) — liệt kê rồi
  // không gỡ (--keep-hooks/--no-backup) là nói dối user.
  if (canPruneHooks && reposWithAvatarHook.length > 0) {
    log.plain(
      `  ${chalk.red("✗")} pre-push hook Avatar (src/<repo>/.git/hooks/pre-push) ở ${reposWithAvatarHook.length} repo: ${reposWithAvatarHook.join(", ")}`,
    );
  }
  if (artifacts.gitignorePath) log.plain(`  ${chalk.yellow("✎")} .gitignore (gỡ Avatar block)`);
  // Avatar block ở src/<repo> (#138 phase-04). Chỉ liệt kê khi THẬT SỰ sẽ gỡ
  // (canPrune) — liệt kê rồi không gỡ là nói dối user, đúng luật dòng hook v3.
  if (ignoreScan.canPrune) {
    if (ignoreScan.exclude.uncheckable) {
      // CẤM in dòng ✓ "không có gì để dọn" ở đây: danh sách rỗng vì KHÔNG KIỂM
      // ĐƯỢC, không phải vì sạch. --dry-run là thứ user tin TRƯỚC khi bấm.
      log.plain(
        `  ${chalk.yellow("⚠")} Không kiểm được repo nào (git không chạy được) — KHÔNG kết luận "không có gì để dọn".`,
      );
    } else if (ignoreScan.exclude.repos.length > 0) {
      log.plain(
        `  ${chalk.yellow("✎")} src/<repo>/.git/info/exclude (gỡ Avatar block) ở ${ignoreScan.exclude.repos.length} repo: ${ignoreScan.exclude.repos.join(", ")}`,
      );
    }
    if (ignoreScan.gitignore.length > 0) {
      log.plain(
        `  ${chalk.yellow("✎")} src/<repo>/.gitignore (gỡ Avatar block sót) ở ${ignoreScan.gitignore.length} repo: ${ignoreScan.gitignore.join(", ")}`,
      );
    }
  }
  if (artifacts.gitmodulesPath && !keepPack) {
    log.plain(`  ${chalk.yellow("✎")} .gitmodules (gỡ entry .claude/pack)`);
  }
  log.plain(`  ${chalk.red("✗")} Global tools (GỠ KHỎI MÁY): gitnexus, gitleaks, trivy`);
  log.plain(
    `    ${chalk.dim("(dùng chung mọi workspace — workspace khác sẽ tự cài lại khi sync)")}`,
  );
  log.plain("");
  log.plain("Không đụng:");
  log.plain(`  ${chalk.green("✓")} src/ (code khách — chỉ gỡ pre-push hook do Avatar ghi)`);
  log.plain(`  ${chalk.green("✓")} Git history`);
  log.plain(`  ${chalk.green("✓")} ~/.avatar/config.json (token SSO)`);
  log.plain(`  ${chalk.green("✓")} Secrets trong keychain`);
  log.plain("");
}

function printUninstallSuccessBox(
  backupPath: string | null,
  prunedHookBackups: { name: string; backupPath: string }[],
): void {
  const lines: string[] = [`${chalk.green("✓")} Avatar đã được gỡ khỏi project`];
  if (backupPath) {
    lines.push("");
    lines.push(`  ${chalk.dim("Backup:")} ${backupPath}`);
    lines.push(`  ${chalk.dim("Restore:")} ${chalk.cyan(`cp -r "${backupPath}"/* .`)}`);
  }
  // Restore riêng cho hook v3 — lệnh cũ (`cp -r .../* .`) đổ <backupPath>/hooks/
  // thành <projectRoot>/hooks/, không repo nào nhận lại được hook. Chỉ in khi
  // thư mục hooks/ THẬT SỰ có file (prunedHookBackups = pruneResult.backups,
  // tức đã backup thành công — không phải mọi repo "có hook lúc snapshot").
  if (prunedHookBackups.length > 0) {
    lines.push("");
    lines.push(`  ${chalk.dim("Restore hook v3 (mỗi repo):")}`);
    for (const b of prunedHookBackups) {
      lines.push(
        `    ${chalk.cyan(
          `cp "${b.backupPath}" src/${b.name}/.git/hooks/pre-push && chmod +x src/${b.name}/.git/hooks/pre-push`,
        )}`,
      );
    }
  }
  process.stdout.write(`${boxen(lines.join("\n"), { padding: 1, borderStyle: "round" })}\n`);
}
